import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import { BookOpen, Save, Clock as ClockIcon, Ban } from 'lucide-react';

const DEFAULT_BANQUE_HEURES = `## Tarifs réguliers des services

Voici le tableau détaillé des tarifs réguliers et avec entente (et/ou achat d'une banque d'heures) révisée annuellement :

### Description des services

- Service Niveau 1 (Installation, configuration et support postes/utilisateurs, M365, imprimantes, téléphonie à l'utilisateur, travail en atelier, etc.) — 105,00 $/h (Hors heures: Temps 1½)
- Service Niveau 2 (Serveur, réseautique, téléphonie IP, hébergement et domaine, monitoring, etc.) — 115,00 $/h (Hors heures: Temps 1½)
- Travaux de câblage ou spécialisés — 125,00 $/h (Hors heures: Temps 1½)
- Audit, réseaux et cybersécurité — 150,00 $/h (Hors heures: Temps 1½)
- Développement applicatif, web et AI — 150,00 $/h (Hors heures: Temps 1½)

### Banques d'heures

- Banque d'heures: Service niveau 1 et 2 — 15 heures — 1 485,00 $
- Banque d'heures: Service niveau 1 et 2 — 25 heures — 2 400,00 $
- Banque d'heures: Service niveau 1 et 2 — 50 heures — 4 650,00 $
- Banque d'heures: Service niveau 1 et 2 — 100 heures — 9 000,00 $

Le tarif avec banque d'heures vous donne accès aux avantages supplémentaires suivants :

- Prise en charge complète; Prise en charge initiale incluse
- Priorité de service et support à distance par tranche de 15 minutes
- Maintenance de service avec calendrier au choix du client
- Documentation interne sécurisé et confidentiel avec accès contrôlés
- Rabais sur matériel et livraison toujours incluse`;

const DEFAULT_EXCLUSIONS = `## Exclusions

Tout ce qui n'est pas inclus dans l'engagement du mandat et les clauses suivantes:

- Le client possède les droits d'autorisations nécessaires aux sites d'installations
- Le client possède les droits de licences d'utilisation pour toutes les licences non inscrites dans la présente offre de service
- Tout problème ou tout manquement relié au réseau électrique et au réseau informatique du client, nécessaire à la réalisation du mandat
- Pour toute la ou les portions qui demandent du passage de câbles réseaux, si requis, les conduits et boites électriques prévues à cet effet seront fournis et installés par l'électricien
- Pour tout branchement ou pour l'installation réseautique, le prix n'inclus pas la location d'un charriot élévateur de type « ciseaux lift » si cela serait nécessaire au présent mandat

## Conclusion

Entreprise peut considérer que Global Ti agit toujours pour faire les interventions en douceur et avec le plus de transparence que possible pour ne pas nuire à ses activités opérationnelles. Si la présente proposition répond à vos besoins et que vous voulez procéder avec le démarrage du projet avec Global Ti, veuillez la signer et nous la retourner par la méthode de communication de votre choix.`;

export default function AnnexeSettingsPage() {
  const [banqueHeures, setBanqueHeures] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingBanque, setSavingBanque] = useState(false);
  const [savingExclusions, setSavingExclusions] = useState(false);
  const [successBanque, setSuccessBanque] = useState(false);
  const [successExclusions, setSuccessExclusions] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnnexeContent();
  }, []);

  const fetchAnnexeContent = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/pdf-annexe', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanqueHeures(data.banqueHeures || DEFAULT_BANQUE_HEURES);
        setExclusions(data.exclusions || DEFAULT_EXCLUSIONS);
      }
    } catch (err) {
      console.error('Fetch annexe content error:', err);
    }
    setLoading(false);
  };

  const handleSaveBanqueHeures = async () => {
    setSavingBanque(true);
    setError(null);
    setSuccessBanque(false);

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/pdf-annexe', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ banqueHeures })
      });

      if (response.ok) {
        setSuccessBanque(true);
        setTimeout(() => setSuccessBanque(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
    setSavingBanque(false);
  };

  const handleSaveExclusions = async () => {
    setSavingExclusions(true);
    setError(null);
    setSuccessExclusions(false);

    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/settings/pdf-annexe', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ exclusions })
      });

      if (response.ok) {
        setSuccessExclusions(true);
        setTimeout(() => setSuccessExclusions(false), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
    setSavingExclusions(false);
  };

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BookOpen size={28} color="#6366f1" />
        Annexe PDF
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configurez le contenu de l'annexe qui apparaît à la fin des devis PDF lorsque l'option est activée.
        Vous pouvez utiliser la syntaxe Markdown : ## Titre, ### Sous-titre, - Liste, **gras**.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Section 1: Banque d'heures */}
      <Card sx={{ p: 3, bgcolor: 'background.paper', backgroundImage: 'none', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ClockIcon size={20} color="#6366f1" />
          Tarifs et banques d'heures
        </Typography>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {successBanque && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Section banque d'heures sauvegardée avec succès
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Contenu de la section banque d'heures
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Informations sur les tarifs réguliers, les banques d'heures et les avantages associés.
            </Typography>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              value={banqueHeures}
              onChange={(e) => setBanqueHeures(e.target.value)}
              fullWidth
              placeholder={"## Tarifs banques d'heures\n\nVoici le tableau détaillé des tarifs réguliers et avec entente...\n\n- Prise en charge complète\n- Priorité de service\n- Support à distance"}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem'
                }
              }}
            />
          </Box>
        </Stack>

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            variant="contained"
            onClick={handleSaveBanqueHeures}
            disabled={savingBanque}
            startIcon={<Save size={18} />}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {savingBanque ? 'Sauvegarde...' : 'Sauvegarder la section'}
          </Button>
        </Box>
      </Card>

      {/* Section 2: Exclusions */}
      <Card sx={{ p: 3, bgcolor: 'background.paper', backgroundImage: 'none' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Ban size={20} color="#ef4444" />
          Exclusions
        </Typography>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

        {successExclusions && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Section exclusions sauvegardée avec succès
          </Alert>
        )}

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Contenu de la section exclusions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Éléments non inclus dans l'engagement du mandat et clauses d'exclusion.
            </Typography>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              value={exclusions}
              onChange={(e) => setExclusions(e.target.value)}
              fullWidth
              placeholder={"## Exclusions\n\nTout ce qui n'est pas inclus dans l'engagement du mandat...\n\n- Droits d'autorisations nécessaires aux sites d'installations\n- Licences d'utilisation non inscrites dans l'offre"}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.85rem'
                }
              }}
            />
          </Box>
        </Stack>

        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            variant="contained"
            onClick={handleSaveExclusions}
            disabled={savingExclusions}
            startIcon={<Save size={18} />}
            sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
          >
            {savingExclusions ? 'Sauvegarde...' : 'Sauvegarder la section'}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
